import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Close, DeleteOutline } from '@mui/icons-material';
import SavingOverlay from '../SavingOverlay';
import { colors, typography } from '../../theme/designSystem';
import { getDefaultInputLabelProps, getDefaultTextFieldSx, getMultilineInputLabelProps, getMultilineTextFieldSx } from '../../theme/textFieldStyles';
import { patientsService } from '../../services/patientsService';
import { entityUserService } from '../../services/entityUserService';
import { ContractRow } from '../../services/contractsService';
import { contractSignatureService, ContractSignerInput, SignatureChannel, signatureErrorMessage } from '../../services/signatureService';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Enviar contrato para assinatura eletrônica (RN-558c). Os assinantes vêm preenchidos do cadastro
 * (responsável; sem responsável, o próprio paciente) e podem ser ajustados. Cada um assina pelo canal
 * escolhido: e-mail ou WhatsApp. A ordem define quem assina primeiro; mesma ordem = em paralelo.
 * O PDF é o mesmo da impressão (template da clínica em toda folha), gerado no servidor.
 */
interface Props {
  open: boolean;
  contract: ContractRow | null;
  onClose: () => void;
  onSent: () => void;
}

interface SignerDraft extends ContractSignerInput {
  key: number;
}

let nextKey = 1;

const blank = (order: number, role = ''): SignerDraft => ({
  key: nextKey++,
  role,
  name: '',
  email: null,
  phone: null,
  documentNumber: null,
  channel: 'email',
  order,
});

const SendForSignatureModal: React.FC<Props> = ({ open, contract, onClose, onSent }) => {
  const { user } = useAuth();
  const entityId = user?.entityId || '';
  const [signers, setSigners] = useState<SignerDraft[]>([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !contract) return;
    setError('');
    setMessage('');
    setLoading(true);
    const phoneOf = (ddd?: string | null, number?: string | null) => (number ? `${ddd ? `(${ddd}) ` : ''}${number}` : null);

    // Contrato do profissional: o assinante é o próprio vínculo (nome, e-mail de acesso, telefone, CPF)
    if (contract.professionalRegistrationId) {
      const registrationId = contract.professionalRegistrationId;
      entityUserService.getByEntity(entityId)
        .then((users) => {
          const link = users.find((u) => u.ueprId === registrationId);
          setSigners([{
            ...blank(1, 'Contratado'),
            name: link?.name || '',
            email: link?.email || null,
            phone: phoneOf(link?.phoneCode, link?.phoneNumber),
            documentNumber: link?.document || null,
          }]);
        })
        .catch(() => setSigners([blank(1, 'Contratado')]))
        .finally(() => setLoading(false));
      return;
    }

    patientsService.getById(contract.patientId ?? '')
      .then((p) => {
        const responsible = (p.responsibleName1 || '').trim();
        const first: SignerDraft = responsible
          ? {
              ...blank(1, 'Responsável financeiro'),
              name: responsible,
              email: p.responsibleEmail1 || null,
              phone: phoneOf(p.responsiblePhoneCode1, p.responsiblePhone1),
              documentNumber: p.responsibleDocument1 || null,
            }
          : {
              ...blank(1, 'Contratante'),
              name: p.patientName || '',
              email: p.patientEmail || null,
              phone: phoneOf(p.patientPhoneCode, p.patientPhone),
              documentNumber: p.patientDocument || null,
            };
        setSigners([first]);
      })
      .catch(() => setSigners([blank(1, 'Responsável financeiro')]))
      .finally(() => setLoading(false));
  }, [open, contract, entityId]);

  const update = (key: number, patch: Partial<SignerDraft>) =>
    setSigners((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));

  const validate = (): string => {
    if (signers.length === 0) return 'Informe pelo menos um assinante.';
    for (const s of signers) {
      if (!s.name.trim()) return 'Todo assinante precisa de nome.';
      if (!s.role.trim()) return `Informe o papel de ${s.name} no contrato.`;
      if (s.channel === 'email' && !(s.email || '').trim()) return `${s.name} recebe por e-mail: informe o e-mail.`;
      if (s.channel === 'whatsapp' && !(s.phone || '').trim()) return `${s.name} recebe por WhatsApp: informe o celular.`;
    }
    return '';
  };

  const send = async () => {
    if (!contract) return;
    const problem = validate();
    if (problem) { setError(problem); return; }
    setError('');
    setSending(true);
    try {
      await contractSignatureService.send(
        contract.id,
        signers.map(({ key, ...s }) => ({
          ...s,
          name: s.name.trim(),
          role: s.role.trim(),
          email: s.email?.trim() || null,
          phone: s.phone?.trim() || null,
          documentNumber: s.documentNumber?.trim() || null,
        })),
        message.trim() || null,
      );
      onSent();
    } catch (e) {
      setError(signatureErrorMessage(e, 'Não foi possível enviar para assinatura.'));
    } finally {
      setSending(false);
    }
  };

  const fieldSx = getDefaultTextFieldSx();
  const labelProps = getDefaultInputLabelProps();

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden', position: 'relative' } }}>
      <SavingOverlay open={sending} message="Gerando o PDF e enviando..." />
      <DialogTitle sx={{ backgroundColor: colors.primary, color: colors.white, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem' }}>
        <Typography component="span" sx={{ fontSize: '1.4rem', fontWeight: typography.fontWeight.semibold }}>Enviar para assinatura</Typography>
        <IconButton onClick={onClose} disabled={sending} sx={{ color: colors.white, padding: '0.25rem' }} aria-label="Fechar"><Close /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: '1.5rem !important' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} sx={{ color: colors.primary }} /></Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ fontSize: '0.85rem', color: colors.textSecondary }}>
              <strong>{contract?.templateTitle}</strong> · versão {contract?.templateVersion}. Cada assinante recebe o link pelo canal escolhido.
              Assinantes com a mesma ordem assinam ao mesmo tempo; ordens diferentes, um depois do outro.
            </Typography>

            {signers.map((s, index) => (
              <Box key={s.key} sx={{ border: `1px solid ${colors.backgroundAlt}`, borderRadius: '8px', p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: colors.textStrong }}>Assinante {index + 1}</Typography>
                  {signers.length > 1 && (
                    <Tooltip title="Remover assinante" arrow>
                      <IconButton size="small" onClick={() => setSigners((prev) => prev.filter((x) => x.key !== s.key))} sx={{ color: colors.error }} aria-label={`Remover assinante ${index + 1}`}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1.6fr 0.9fr 0.6fr' }, gap: 1.5 }}>
                  <TextField size="small" label="Papel no contrato" value={s.role} onChange={(e) => update(s.key, { role: e.target.value })} placeholder="Ex.: Responsável financeiro" inputProps={{ maxLength: 60 }} InputLabelProps={labelProps} sx={fieldSx} />
                  <TextField size="small" label="Nome" value={s.name} onChange={(e) => update(s.key, { name: e.target.value })} inputProps={{ maxLength: 200 }} InputLabelProps={labelProps} sx={fieldSx} />
                  <TextField select size="small" label="Recebe por" value={s.channel} onChange={(e) => update(s.key, { channel: e.target.value as SignatureChannel })} InputLabelProps={labelProps} sx={fieldSx}>
                    <MenuItem value="email">E-mail</MenuItem>
                    <MenuItem value="whatsapp">WhatsApp</MenuItem>
                  </TextField>
                  <TextField size="small" type="number" label="Ordem" value={s.order} onChange={(e) => update(s.key, { order: Math.max(1, Number(e.target.value) || 1) })} inputProps={{ min: 1, max: 10 }} InputLabelProps={labelProps} sx={fieldSx} />
                  <TextField size="small" label="E-mail" value={s.email ?? ''} onChange={(e) => update(s.key, { email: e.target.value })} inputProps={{ maxLength: 200 }} InputLabelProps={labelProps} sx={fieldSx} />
                  <TextField size="small" label="Celular" value={s.phone ?? ''} onChange={(e) => update(s.key, { phone: e.target.value })} placeholder="(11) 99999-0000" InputLabelProps={labelProps} sx={fieldSx} />
                  <TextField size="small" label="CPF (opcional)" value={s.documentNumber ?? ''} onChange={(e) => update(s.key, { documentNumber: e.target.value })} inputProps={{ maxLength: 18 }} InputLabelProps={labelProps} sx={fieldSx} />
                </Box>
              </Box>
            ))}

            <Box>
              <Button
                size="small"
                startIcon={<Add />}
                onClick={() => setSigners((prev) => [...prev, blank(prev.length + 1, prev.length === 0 ? 'Responsável financeiro' : 'Clínica')])}
                disabled={signers.length >= 10}
                sx={{ textTransform: 'none', color: colors.primaryText }}
              >
                Adicionar assinante
              </Button>
            </Box>

            <TextField
              size="small"
              label="Mensagem no convite (opcional)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Ex.: Olá! Segue o contrato de atendimento para sua assinatura."
              multiline
              minRows={2}
              inputProps={{ maxLength: 500 }}
              InputLabelProps={getMultilineInputLabelProps()}
              sx={getMultilineTextFieldSx()}
            />
            <Typography sx={{ fontSize: '0.75rem', color: colors.textSecondary }}>
              O envio por WhatsApp depende do plano da clínica no provedor de assinatura. Depois de assinado, o PDF assinado e a
              prova da assinatura ficam guardados no sistema.
            </Typography>

            {error && <Typography sx={{ color: colors.error, fontSize: '0.85rem' }}>{error}</Typography>}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: '1rem 1.5rem', gap: 1 }}>
        <Button onClick={onClose} variant="outlined" disabled={sending} sx={{ textTransform: 'none', borderColor: colors.border, color: colors.textSecondary }}>Cancelar</Button>
        <Button onClick={send} variant="contained" disabled={sending || loading} sx={{ textTransform: 'none', backgroundColor: colors.primary, '&:hover': { backgroundColor: colors.primaryHover } }}>
          Enviar para assinatura
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendForSignatureModal;
